/**
 * Oportunidades perdidas v2 — Sprint 16.
 *
 * Refactor de "lista plana cronológica" a "playbook con patrones":
 * - Hero con 3 números (conteo + techo + capturado por peers).
 * - Capa Patrones (el wow): top 3 sectores con causa heurística.
 * - Timeline de barras por mes (recharts).
 * - Lista enriquecida con peer captures + causa por card + "No aplicaba",
 *   que se puede deshacer desde la misma tarjeta (#712). "Guardar template"
 *   salió en #712: nada leía los templates.
 * - Sidebar con filtros + toggle "vs peers".
 *
 * Telemetría: `lost_opp_viewed`, `lost_pattern_clicked`,
 * `lost_marked_not_applicable`, `lost_not_applicable_undone`,
 * `lost_filter_applied` (Sprint 16.5 consolidará).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import {
  ApiError,
  deleteLostOpportunityAction,
  fetchLostOpportunities,
  postLostOpportunityAction,
  type LostOpportunitiesReportDTO,
  type LostOpportunityItemDTO,
  type LostPatternDTO,
  type PeerCaptureDTO,
} from "@/lib/api";
import { track } from "@/lib/analytics";
import { formatDateEs, parseIsoDate } from "@/lib/dates";
import { useActiveMunicipalityDane } from "@/lib/municipality";

interface MarcadaEnLista {
  item: LostOpportunityItemDTO;
  /** Posición en la lista visible cuando se marcó. */
  index: number;
}
type Marcadas = ReadonlyMap<string, MarcadaEnLista>;
const SIN_MARCADAS: Marcadas = new Map();

const WINDOWS = [
  { value: 12, label: "12m" },
  { value: 24, label: "24m" },
  { value: 36, label: "36m" },
  { value: 60, label: "5 años" },
];

const CAUSE_LABELS: Record<LostPatternDTO["causa"], string> = {
  ventana_corta: "Ventana corta",
  sector_debil: "Sector débil del muni",
  concentracion_temporal: "Concentración temporal",
  mixto: "Mixto",
};

const CAUSE_EXPLANATIONS: Record<LostPatternDTO["causa"], string> = {
  ventana_corta:
    "Las convocatorias en este sector tienen ventanas más cortas que la mediana de respuesta del muni.",
  sector_debil:
    "Este sector es débil en el Score Alpha — el muni capta menos que el promedio en él.",
  concentracion_temporal:
    "Las convocatorias se concentran en pocos meses; vale planificar el ciclo.",
  mixto: "Sin causa clara identificable.",
};

function formatCopCompact(raw: string | null | undefined): string {
  if (!raw) return "$0";
  const n = Number(raw);
  if (Number.isNaN(n) || n === 0) return "$0";
  if (n >= 1_000_000_000_000) return `$${(n / 1_000_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}MM`;
  if (n >= 1_000_000) return `$${Math.round(n / 1_000_000)}M`;
  return `$${n.toLocaleString("es-CO")}`;
}

function formatDate(iso: string): string {
  // `closes_at` es `date` sin hora: parseo local, no UTC (#537).
  return formatDateEs(iso, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function OportunidadesPerdidas() {
  const activeDane = useActiveMunicipalityDane();
  const [months, setMonths] = useState<number>(24);
  const [sectorFilter, setSectorFilter] = useState<string | null>(null);
  // "No aplicaba" marcadas en esta visita (#712): el reporte ya no las trae,
  // pero la tarjeta se queda en su lugar con "Deshacer" hasta que recargues.
  // Van atadas a la lista en que se marcaron: otra ventana u otro municipio
  // es otra lista, y una marca no se arrastra a ella.
  const lista = `${activeDane}|${months}`;
  const [marcadasDeLista, setMarcadasDeLista] = useState<{ lista: string; mapa: Marcadas }>(() => ({
    lista,
    mapa: SIN_MARCADAS,
  }));
  const marcadas = marcadasDeLista.lista === lista ? marcadasDeLista.mapa : SIN_MARCADAS;
  const viewedRef = useRef(false);

  // Sprint 18 — H-1: el endpoint ya no acepta tenant_id del cliente; lo
  // deriva del JWT vía ensure_context. Si el user no es miembro del muni,
  // devuelve 403 y la página muestra error. No necesitamos fetch separado
  // del onboarding context para conocer el tenant.
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["lost-opportunities", activeDane, months],
    queryFn: () =>
      fetchLostOpportunities(activeDane, {
        months,
        withPatterns: true,
        withPeerCaptures: true,
      }),
    retry: (count, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 400 || err.status === 403)
      )
        return false;
      return count < 2;
    },
  });

  useEffect(() => {
    if (data && !viewedRef.current) {
      track({
        event_name: "lost_opp_viewed",
        properties: {
          dane_code: activeDane,
          months,
          count: data.count,
          patterns_count: data.patterns.length,
        },
      });
      viewedRef.current = true;
    }
  }, [data, activeDane, months]);

  const filas = useMemo(() => {
    if (!data) return [];
    const lista = data.items
      .filter((item) => !marcadas.has(item.opportunity_id))
      .map((item) => ({ item, marcada: false }));
    const enOrden = [...marcadas.values()].sort((a, b) => a.index - b.index);
    for (const { item, index } of enOrden) {
      lista.splice(Math.min(index, lista.length), 0, { item, marcada: true });
    }
    return lista;
  }, [data, marcadas]);

  const filasVisibles = useMemo(
    () => (sectorFilter ? filas.filter((f) => f.item.sector === sectorFilter) : filas),
    [filas, sectorFilter],
  );

  function cambiarMarcadas(cambio: (mapa: Map<string, MarcadaEnLista>) => void) {
    setMarcadasDeLista((previas) => {
      const mapa = new Map(previas.lista === lista ? previas.mapa : SIN_MARCADAS);
      cambio(mapa);
      return { lista, mapa };
    });
  }

  function onMarcada(item: LostOpportunityItemDTO) {
    // Su posición en la lista visible: ahí se queda la fila con "Deshacer".
    const index = filas.findIndex((f) => f.item.opportunity_id === item.opportunity_id);
    cambiarMarcadas((mapa) =>
      mapa.set(item.opportunity_id, { item, index: index === -1 ? filas.length : index }),
    );
  }

  function onRestaurada(opportunityId: string) {
    cambiarMarcadas((mapa) => mapa.delete(opportunityId));
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
          Oportunidades perdidas
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Convocatorias relevantes que cerraron en la ventana — qué dejaste pasar, por qué, y qué
          hacer al respecto.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
          Ventana
        </span>
        {WINDOWS.map((w) => (
          <button
            key={w.value}
            type="button"
            onClick={() => {
              setMonths(w.value);
              track({
                event_name: "lost_filter_applied",
                properties: { filter: "window", value: w.value },
              });
            }}
            aria-pressed={months === w.value}
            className={`cursor-pointer rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              months === w.value
                ? "bg-brand-700 text-white"
                : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            {w.label}
          </button>
        ))}
      </div>

      {isLoading && <Skeleton />}
      {isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">
          Error: {error instanceof Error ? error.message : "desconocido"}
        </div>
      )}

      {data && (
        <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
          {/* <div>, no <main>: el landmark main lo provee AppShell y anidar
              otro es ARIA invalido (#497). */}
          <div className="space-y-5">
            <HeroV2 data={data} />

            {data.patterns.length > 0 && (
              <PatternsCard
                patterns={data.patterns}
                onClickSector={(sector) => {
                  track({
                    event_name: "lost_pattern_clicked",
                    properties: { pattern_sector: sector },
                  });
                  setSectorFilter(sector);
                }}
              />
            )}

            {data.items.length > 0 && <Timeline items={data.items} months={months} />}

            {data.items.length === 0 && marcadas.size === 0 ? (
              <EmptyState minScore={data.min_score} />
            ) : (
              <section>
                {sectorFilter && (
                  <div className="mb-3 flex items-center justify-between rounded-md bg-brand-50 px-3 py-2 text-xs text-brand-800">
                    <span>
                      Filtrando por sector: <strong>{sectorFilter}</strong> ·{" "}
                      {filasVisibles.filter((f) => !f.marcada).length} items
                    </span>
                    <button
                      type="button"
                      onClick={() => setSectorFilter(null)}
                      className="cursor-pointer font-semibold underline-offset-2 hover:underline"
                    >
                      Quitar
                    </button>
                  </div>
                )}
                <ul data-testid="lost-opportunities-list" className="space-y-3">
                  {filasVisibles.map(({ item, marcada }) =>
                    marcada ? (
                      <MarcadaRow
                        key={item.opportunity_id}
                        item={item}
                        municipality={activeDane}
                        onRestaurada={onRestaurada}
                      />
                    ) : (
                      <LostRowV2
                        key={item.opportunity_id}
                        item={item}
                        peerCaptures={data.peer_captures_by_opp[item.opportunity_id] ?? []}
                        municipality={activeDane}
                        tenantConnected={true}
                        onMarcada={onMarcada}
                      />
                    ),
                  )}
                </ul>
              </section>
            )}
          </div>

          <Sidebar count={data.count} sectorActive={sectorFilter} patterns={data.patterns} />
        </div>
      )}
    </div>
  );
}

function HeroV2({ data }: { data: LostOpportunitiesReportDTO }) {
  return (
    <section
      data-testid="lost-hero"
      className="rounded-2xl border border-amber-200 bg-linear-to-br from-amber-50 to-white p-5 shadow-xs"
    >
      <p className="text-[11px] font-semibold tracking-wide text-amber-800 uppercase">
        Últimos {data.months_window} meses
      </p>
      <div className="mt-3 grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div>
          <p className="text-xs text-slate-500">Oportunidades perdidas</p>
          <p className="text-3xl font-extrabold text-slate-900">{data.count}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Techo dejado (no ganado)</p>
          <p className="text-3xl font-extrabold text-amber-800">
            {formatCopCompact(data.total_amount_per_project_cop)}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Capturado por peers similares</p>
          <p className="text-3xl font-extrabold text-emerald-700">
            {formatCopCompact(data.estimated_peer_captured_cop)}
          </p>
        </div>
      </div>
      <p className="mt-3 border-t border-amber-100 pt-3 text-xs text-slate-500">
        El tercer número es lo que <strong>peers similares de tu departamento</strong> sí captaron
        en los mismos sectores — estimación realista de mercado, no techo teórico.
      </p>
    </section>
  );
}

function PatternsCard({
  patterns,
  onClickSector,
}: {
  patterns: LostPatternDTO[];
  onClickSector: (sector: string) => void;
}) {
  return (
    <section
      data-testid="lost-patterns-card"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs"
    >
      <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
        <span aria-hidden="true">🔍</span>
        Patrones detectados
      </h2>
      <p className="mt-1 text-xs text-slate-500">
        Donde el muni pierde repetidamente. Click en un sector para filtrar la lista.
      </p>
      <ul className="mt-4 space-y-3" role="list">
        {patterns.map((p) => (
          <PatternRow key={p.sector} pattern={p} onClick={() => onClickSector(p.sector)} />
        ))}
      </ul>
    </section>
  );
}

function PatternRow({ pattern, onClick }: { pattern: LostPatternDTO; onClick: () => void }) {
  const sectorLower = pattern.sector.toLowerCase();
  const showCause = pattern.causa !== "mixto" && pattern.causa_confidence >= 0.4;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid="pattern-row"
        data-sector={pattern.sector}
        className="group w-full cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-3 text-left transition-all hover:border-brand-300 hover:bg-brand-50/30 hover:shadow-xs"
      >
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-semibold text-slate-900">
            Sector <span className="text-brand-700">{sectorLower}</span>
          </p>
          <span className="text-xs font-medium text-slate-500">
            {pattern.count} opps · {formatCopCompact(pattern.total_per_project_cop)} techo
          </span>
        </div>
        {showCause && (
          <p className="mt-1.5 text-xs text-slate-600">
            <span className="font-semibold">Causa probable:</span> {CAUSE_LABELS[pattern.causa]} —{" "}
            {CAUSE_EXPLANATIONS[pattern.causa]}
          </p>
        )}
        <p className="mt-2 text-xs font-semibold text-brand-700 group-hover:underline">
          Filtrar lista por {sectorLower} →
        </p>
      </button>
    </li>
  );
}

function Timeline({ items, months }: { items: LostOpportunityItemDTO[]; months: number }) {
  // Agrupar por YYYY-MM
  const data = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const it of items) {
      // Parseo local: una que cerro el 1 del mes no cae en el bucket anterior (#537).
      const d = parseIsoDate(it.closes_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      counts[key] = (counts[key] ?? 0) + 1;
    }
    // Generar buckets para los últimos N meses (ordenados ASC).
    const now = new Date();
    const buckets: Array<{ month: string; count: number }> = [];
    for (let i = months - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      buckets.push({ month: key.slice(2), count: counts[key] ?? 0 });
    }
    return buckets;
  }, [items, months]);

  return (
    <section
      data-testid="lost-timeline"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs"
    >
      <h2 className="text-base font-bold text-slate-900">Timeline de pérdidas</h2>
      <p className="mt-1 text-xs text-slate-500">
        Oportunidades perdidas por mes — útil para ver concentraciones temporales.
      </p>
      <div className="mt-3" style={{ width: "100%", height: 180 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: "#64748b" }}
              interval="preserveStartEnd"
            />
            <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
            <Tooltip
              contentStyle={{
                fontSize: 12,
                border: "1px solid #e2e8f0",
                borderRadius: 8,
              }}
              labelStyle={{ color: "#0f172a", fontWeight: 600 }}
            />
            <Bar dataKey="count" fill="#1d4ed8" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function LostRowV2({
  item,
  peerCaptures,
  municipality,
  tenantConnected,
  onMarcada,
}: {
  item: LostOpportunityItemDTO;
  peerCaptures: PeerCaptureDTO[];
  municipality: string;
  tenantConnected: boolean;
  onMarcada: (item: LostOpportunityItemDTO) => void;
}) {
  const queryClient = useQueryClient();
  const notApplicableMutation = useMutation({
    mutationFn: () =>
      postLostOpportunityAction({
        opportunityId: item.opportunity_id,
        municipality,
        action: "not_applicable",
      }),
    onSuccess: async () => {
      track({
        event_name: "lost_marked_not_applicable",
        properties: { opp_id: item.opportunity_id, sector: item.sector },
      });
      // Primero la tarjeta pasa a "Deshacer" en su lugar; después el
      // reporte se refresca y los números ya no la cuentan (#712).
      onMarcada(item);
      await queryClient.invalidateQueries({
        queryKey: ["lost-opportunities", municipality],
      });
    },
  });

  return (
    <li
      data-testid="lost-card"
      className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs"
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-slate-700 uppercase">
          {item.source_name}
        </span>
        {item.sector && (
          <span className="rounded-full bg-brand-50 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-brand-700 uppercase">
            {item.sector}
          </span>
        )}
        <span className="ml-auto font-mono text-xs text-slate-500">
          Match {item.match_score}% · cerró {formatDate(item.closes_at)}
        </span>
      </div>

      <h2 className="text-sm font-bold text-slate-900">{item.title}</h2>

      {/* Un techo desconocido no es cero: con NULL decimos "sin dato" y, si
          la convocatoria trae monto total, lo mostramos etiquetado como tal.
          El hero ya excluye estas de la suma, asi card y hero coinciden (#618). */}
      <p className="mt-1 text-xs text-slate-500">
        Techo por proyecto:{" "}
        <span className="font-medium">
          {item.amount_per_project_max_cop
            ? formatCopCompact(item.amount_per_project_max_cop)
            : "sin dato"}
        </span>
        {!item.amount_per_project_max_cop && item.amount_total_cop && (
          <>
            {" "}
            · Monto total:{" "}
            <span className="font-medium">{formatCopCompact(item.amount_total_cop)}</span>
          </>
        )}
      </p>

      {peerCaptures.length > 0 && (
        <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2">
          <p className="text-[11px] font-semibold tracking-wide text-emerald-800 uppercase">
            Capturado por peers similares
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-slate-700">
            {peerCaptures.slice(0, 3).map((p) => (
              <li key={p.dane_code}>
                · <span className="font-medium">{p.name}</span>
                {p.amount_cop && (
                  <span className="text-slate-500"> — {formatCopCompact(p.amount_cop)}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {item.sector && (
          <Link
            to={`/radar?sector=${encodeURIComponent(item.sector.toLowerCase())}`}
            onClick={() =>
              track({
                event_name: "lost_cta_clicked",
                properties: { opp_id: item.opportunity_id, sector: item.sector },
              })
            }
            className="cursor-pointer rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white shadow-xs transition-colors hover:bg-brand-800"
            data-testid="lost-cta-radar"
          >
            Ver convocatorias similares →
          </Link>
        )}
        {tenantConnected && (
          <>
            <button
              type="button"
              onClick={() => notApplicableMutation.mutate()}
              disabled={notApplicableMutation.isPending}
              className="cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
              data-testid="lost-not-applicable"
            >
              No aplicaba
            </button>
          </>
        )}
      </div>
    </li>
  );
}

/**
 * Una convocatoria que el municipio acaba de marcar como "No aplicaba"
 * (#712). Se queda en su lugar hasta que recargue, con la salida para un clic
 * equivocado: antes desaparecía del reporte para siempre.
 */
function MarcadaRow({
  item,
  municipality,
  onRestaurada,
}: {
  item: LostOpportunityItemDTO;
  municipality: string;
  onRestaurada: (opportunityId: string) => void;
}) {
  const queryClient = useQueryClient();
  const undo = useMutation({
    mutationFn: () =>
      deleteLostOpportunityAction({
        opportunityId: item.opportunity_id,
        municipality,
        action: "not_applicable",
      }),
    onSuccess: async () => {
      track({
        event_name: "lost_not_applicable_undone",
        properties: { opp_id: item.opportunity_id, sector: item.sector },
      });
      // La tarjeta vuelve cuando el reporte ya la trae: sin esperar, la fila
      // desaparecería un instante antes de reaparecer.
      await queryClient.invalidateQueries({
        queryKey: ["lost-opportunities", municipality],
      });
      onRestaurada(item.opportunity_id);
    },
  });

  return (
    <li
      data-testid="lost-card-marcada"
      className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4"
    >
      <p className="text-sm text-slate-700">
        Marcaste <strong className="text-slate-900">{item.title}</strong> como «No aplicaba»: ya no
        cuenta en tus oportunidades perdidas ni en los patrones.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => undo.mutate()}
          disabled={undo.isPending}
          className="cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-50"
          data-testid="lost-undo-not-applicable"
        >
          {undo.isPending ? "Deshaciendo…" : "Deshacer"}
        </button>
        {undo.isError && (
          <p role="alert" className="text-xs text-red-700">
            No se pudo deshacer: la convocatoria sigue marcada como «No aplicaba». Intenta de nuevo.
          </p>
        )}
      </div>
    </li>
  );
}

function Sidebar({
  count,
  sectorActive,
  patterns,
}: {
  count: number;
  sectorActive: string | null;
  patterns: LostPatternDTO[];
}) {
  return (
    <aside className="lg:sticky lg:top-4 lg:self-start">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs">
        <h3 className="text-xs font-bold tracking-wide text-slate-500 uppercase">Resumen</h3>
        <p className="mt-2 text-2xl font-extrabold text-slate-900 tabular-nums">{count}</p>
        <p className="text-xs text-slate-500">opps perdidas en la ventana</p>
        {sectorActive && (
          <p className="mt-2 text-[11px] text-brand-700">
            Filtrando: <strong>{sectorActive.toLowerCase()}</strong>
          </p>
        )}
      </div>

      {patterns.length > 0 && (
        <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4">
          <h3 className="text-xs font-bold tracking-wide text-slate-500 uppercase">Top sectores</h3>
          <ul className="mt-2 space-y-1.5">
            {patterns.map((p) => (
              <li key={p.sector} className="flex items-baseline justify-between text-xs">
                <span className="font-medium text-slate-700">{p.sector.toLowerCase()}</span>
                <span className="text-slate-500 tabular-nums">{p.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4">
        <h3 className="text-xs font-bold tracking-wide text-slate-500 uppercase">
          Cómo se calcula
        </h3>
        <p className="mt-2 text-xs leading-relaxed text-slate-600">
          Reproducimos el matching contra cada convocatoria cerrada en la ventana. Si tu Score
          retroactivo era ≥60%, contaba como "perdida". Los peer captures vienen del histórico SGR
          del depto.
        </p>
      </div>
    </aside>
  );
}

function EmptyState({ minScore }: { minScore: number }) {
  return (
    <div
      data-testid="lost-empty"
      className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center"
    >
      <p className="text-sm font-medium text-slate-700">
        Sin oportunidades perdidas con score ≥ {minScore} en esta ventana.
      </p>
      <p className="mt-1 text-xs text-slate-500">
        Prueba una ventana más amplia o explora el Radar para ver convocatorias activas.
      </p>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4">
      <div className="h-32 animate-pulse rounded-2xl bg-slate-100" />
      <div className="h-40 animate-pulse rounded-2xl bg-slate-50" />
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-24 animate-pulse rounded-xl bg-slate-50" />
      ))}
    </div>
  );
}
